import { _ as _ts_decorate } from "@swc/helpers/_/_ts_decorate";
import { _ as _ts_metadata } from "@swc/helpers/_/_ts_metadata";
import { action, computed, flow, observable, runInAction } from "mobx";
import { EProductPlanVisibility, routeLocationPaddleCheckoutPatch, routeLocationPaddleIncompleteCheckoutIsCompleteGet, routeLocationPaddleIncompleteCheckoutPatch, routeLocationPaddleIncompleteCheckoutPost } from "@devowl-wp/api-real-commerce";
import { RealCommerceError } from "../exceptions/realCommerceError.js";
import { getAffiliateFromStorage } from "../others/getAffiliateFromStorage.js";
import { CheckoutStep } from "../store/checkoutV1.js";
import { formatNumber } from "../utils/getDecimalSeparator.js";
import { __ } from "../utils/i18n.js";
import { realCommerceRequest } from "../utils/realCommerceRequest.js";
const RETRY_CHECKOUT_PATCH_RETRIES = 29;
const RETRY_CHECKOUT_PATCH_WAIT = 3000;
class Transaction {
    get isRecovered() {
        return !!this.recoveredIncompleteCheckout;
    }
    get store() {
        return this.product.store;
    }
    get awaitingPaymentRetryImageAndText() {
        const urlPrefix = `${this.store.rootStore.optionStore.publicUrl}images/delayed-payment/`;
        const { awaitingPaymentRetry } = this;
        if (awaitingPaymentRetry <= 3) {
            return [
                undefined,
                __("We are waiting for the payment confirmation. Be patient for a moment...")
            ];
        } else if (awaitingPaymentRetry <= 6) {
            return [
                `${urlPrefix}1.jpg`,
                __("Confirmation of payment takes longer than usual. Please have a little patience...")
            ];
        } else if (awaitingPaymentRetry <= 10) {
            return [
                `${urlPrefix}2.jpg`,
                __("We have found the cable through which the payment must. Now it just has to go through...")
            ];
        } else if (awaitingPaymentRetry <= 15) {
            return [
                `${urlPrefix}3.jpg`,
                __("Knot in the cable found! Now your payment can finally travel through the vastness of the internet...")
            ];
        } else {
            return [
                `${urlPrefix}4.jpg`,
                __("Your payment is on its way. Hopefully it will be confirmed soon...")
            ];
        }
    }
    get currentInitialPrice() {
        return typeof this.initialPrice === "number" ? this.initialPrice : this.product.data.productPlan.price[0].initialPrice;
    }
    get currentRecurringPrice() {
        return typeof this.recurringPrice === "number" ? this.recurringPrice : this.product.data.productPlan.price[0].recurringPrice;
    }
    /**
     * Coupon savings in percent. Returns `0` if no price difference.
     */ get couponSavings() {
        const { initialPrice } = this;
        const [{ initialPrice: originalInitialPrice }] = this.product.data.productPlan.price;
        if (this.lastAppliedCoupon !== null) {
            const percent = 100 - Math.round(initialPrice / originalInitialPrice * 100);
            return percent;
        }
        return 0;
    }
    get labelPrice() {
        const { data: { productPlan: { price: [{ currency }] } } } = this.product;
        return this.initialPriceTax !== undefined ? __("Price (incl. %s %s VAT)", currency, formatNumber(this.initialPriceTax)) : __("Price (incl. VAT)");
    }
    get labelRecurringPrice() {
        const { data: { productPlan: { price: [{ currency }] } }, labelPaymentInterval } = this.product;
        return this.currentRecurringPrice && this.currentInitialPrice !== this.currentRecurringPrice ? __("in the first %s, afterwards %s %s%s per %s", labelPaymentInterval, currency, formatNumber(this.currentRecurringPrice), this.recurringPriceTax === undefined ? "" : __(" (incl. %s %s VAT)", currency, formatNumber(this.recurringPriceTax)), labelPaymentInterval) : "";
    }
    constructor(product, recovered){
        /**
     * When doing `applyPayment` and the payment is not yet processed on the eCommerce system,
     * we will retry every x seconds. This variable holds the number of the current attempt.
     */ this.awaitingPaymentRetry = 0;
        /**
     * Save the last applied coupon of this transaction. This is necessary so we
     * can automatically safe the applied coupon to our backend or delete it.
     */ this.lastAppliedCoupon = null;
        /**
     * Apply the coupon and sync it with the incomplete checkout.
     *
     * @param data
     */ this.syncCouponWithIncompleteCheckout = flow(function*(coupon, throwIfError) {
            if (this.lastAppliedCoupon === coupon) {
                // Already synced
                return;
            }
            this.lastAppliedCoupon = coupon;
            try {
                yield realCommerceRequest({
                    location: routeLocationPaddleIncompleteCheckoutPatch,
                    request: {
                        checkout: {
                            uuid: this.incompleteCheckout.checkout.uuid,
                            coupon
                        }
                    }
                });
                this.lastSyncCouponWithIncompleteCheckoutError = undefined;
            } catch (e) {
                this.lastSyncCouponWithIncompleteCheckoutError = new RealCommerceError(e);
                if (throwIfError) {
                    throw this.lastSyncCouponWithIncompleteCheckoutError;
                }
            }
        });
        /**
     * Remove the current saved VAT information from the current transaction.
     */ this.removePersistedVat = flow(function*(throwIfError) {
            try {
                yield realCommerceRequest({
                    location: routeLocationPaddleIncompleteCheckoutPatch,
                    request: {
                        checkout: {
                            uuid: this.incompleteCheckout.checkout.uuid
                        },
                        vatIdRemovedEventThrown: true
                    }
                });
            } catch (e) {
                if (throwIfError) {
                    throw e;
                }
            }
        });
        /**
     * Set the identification data for this transaction. This must be done AFTER
     * the user has accepted the terms and conditions (including privacy preference, ...).
     * In our case, this is the first step in the checkout form. It also automatically sets
     * the next step in the current checkout of the product.
     *
     * @param data
     */ this.applyIdentification = flow(function*(data, terms, isInvoiceAddressGiven, disableEmailCheck) {
            const { data: { productPlan: { id: planId, price: [fullPrice], product: { id: productId }, visibility }, paddlePayLink } } = this.product;
            const payLinkUuid = paddlePayLink?.uuid || this.recoveredIncompleteCheckout?.checkout.paddlePayLink?.uuid;
            try {
                const affiliateCookie = visibility === EProductPlanVisibility.Private || this.isRecovered ? undefined : getAffiliateFromStorage();
                const incompleteCheckout = yield realCommerceRequest({
                    location: routeLocationPaddleIncompleteCheckoutPost,
                    request: {
                        disableEmailCheck,
                        checkout: {
                            productPlan: {
                                id: planId,
                                fullPrice
                            },
                            product: {
                                id: productId
                            },
                            ...payLinkUuid ? {
                                paddlePayLink: {
                                    uuid: payLinkUuid
                                }
                            } : {},
                            ...data,
                            ...terms
                        },
                        isInvoiceAddressGiven,
                        ...!affiliateCookie ? {} : affiliateCookie.type === "devowl" ? {
                            affiliateLinkView: {
                                hashid: affiliateCookie.hashId
                            }
                        } : {}
                    }
                });
                this.identification = data;
                this.incompleteCheckout = incompleteCheckout;
                this.store.setStep(CheckoutStep.AppliedIdentification);
            } catch (e) {
                throw new RealCommerceError(e);
            }
        });
        /**
     * The payment form got successfully submitted to the payment provider and we can wait for the
     * payment. It automatically retries after x seconds x times until the payment got completed.
     */ this.applyPayment = flow(function*(ecommerceSystemCheckoutId, currentRetry = 0) {
            this.store.setStep(CheckoutStep.AwaitingPayment);
            this.awaitingPaymentRetry = currentRetry;
            const lastCall = currentRetry >= RETRY_CHECKOUT_PATCH_RETRIES;
            try {
                const checkout = yield realCommerceRequest({
                    location: routeLocationPaddleCheckoutPatch,
                    params: {
                        retries: currentRetry,
                        lastCall
                    },
                    request: {
                        checkout: {
                            ecommerceSystemCheckoutId,
                            uuid: this.incompleteCheckout.checkout.uuid
                        }
                    }
                });
                this.incompleteCheckout = undefined;
                this.checkout = checkout;
                this.store.setStep(CheckoutStep.Paid);
            } catch (e) {
                const error = new RealCommerceError(e);
                if (error.hasErrorCode("CheckoutInPaddleWaitForPaymentConfirmation") && !lastCall) {
                    // Wait and try again
                    yield new Promise((resolve)=>setTimeout(resolve, RETRY_CHECKOUT_PATCH_WAIT));
                    yield this.applyPayment(ecommerceSystemCheckoutId, currentRetry + 1);
                } else if (error.hasErrorCode("CheckoutInPaddleNotYetProcessed")) {
                    this.notYetProcessedButShowConfirmationText = error.message;
                    this.store.setStep(CheckoutStep.NotYetProcessedButShowConfirmation);
                } else {
                    this.error = error;
                }
            }
        });
        runInAction(()=>{
            this.product = product;
            this.recoveredIncompleteCheckout = recovered;
        });
    }
    applyNewPrices({ initialPrice, initialPriceTax, recurringPrice, recurringPriceTax, coupon }) {
        this.initialPrice = initialPrice;
        this.initialPriceTax = initialPriceTax;
        this.recurringPrice = recurringPrice;
        this.recurringPriceTax = recurringPriceTax;
        this.syncCouponWithIncompleteCheckout(coupon);
    }
    /**
     * Fetch incomplete order status and return the external eCommerce checkout id.
     */ async fetchIsIncompleteCheckoutComplete() {
        try {
            return await realCommerceRequest({
                location: routeLocationPaddleIncompleteCheckoutIsCompleteGet,
                params: {
                    uuid: this.incompleteCheckout.checkout.uuid
                }
            });
        } catch (e) {
            throw new RealCommerceError(e);
        }
    }
}
_ts_decorate([
    observable,
    _ts_metadata("design:type", typeof Product === "undefined" ? Object : Product)
], Transaction.prototype, "product", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", typeof IRouteResponsePaddleCheckoutPatch === "undefined" ? Object : IRouteResponsePaddleCheckoutPatch)
], Transaction.prototype, "checkout", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", typeof IRouteResponsePaddleIncompleteCheckoutPost === "undefined" ? Object : IRouteResponsePaddleIncompleteCheckoutPost)
], Transaction.prototype, "incompleteCheckout", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", typeof IRouteResponsePaddleIncompleteCheckoutGet === "undefined" ? Object : IRouteResponsePaddleIncompleteCheckoutGet)
], Transaction.prototype, "recoveredIncompleteCheckout", void 0);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], Transaction.prototype, "isRecovered", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], Transaction.prototype, "store", null);
_ts_decorate([
    observable,
    _ts_metadata("design:type", typeof Exception === "undefined" ? Object : Exception)
], Transaction.prototype, "error", void 0);
_ts_decorate([
    observable
], Transaction.prototype, "awaitingPaymentRetry", void 0);
_ts_decorate([
    computed,
    _ts_metadata("design:type", Array),
    _ts_metadata("design:paramtypes", [])
], Transaction.prototype, "awaitingPaymentRetryImageAndText", null);
_ts_decorate([
    observable,
    _ts_metadata("design:type", String)
], Transaction.prototype, "notYetProcessedButShowConfirmationText", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], Transaction.prototype, "initialPrice", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], Transaction.prototype, "initialPriceTax", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], Transaction.prototype, "recurringPrice", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], Transaction.prototype, "recurringPriceTax", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], Transaction.prototype, "identification", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", typeof RealCommerceError === "undefined" ? Object : RealCommerceError)
], Transaction.prototype, "lastSyncCouponWithIncompleteCheckoutError", void 0);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], Transaction.prototype, "currentInitialPrice", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], Transaction.prototype, "currentRecurringPrice", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], Transaction.prototype, "couponSavings", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], Transaction.prototype, "labelPrice", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], Transaction.prototype, "labelRecurringPrice", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        Object
    ]),
    _ts_metadata("design:returntype", void 0)
], Transaction.prototype, "applyNewPrices", null);
export { Transaction, RETRY_CHECKOUT_PATCH_WAIT };
